import React, { useState, useEffect, useMemo } from 'react';
import { Search, ShieldAlert, Award, Star, Settings, UserCheck, Crown, Lock } from 'lucide-react';
import axios from 'axios';
import constant from '../constants/constant';
import { CountryBadge } from '../components/SearchableCountrySelect';

const ROLE_RANKS = {
  'OWNER': 100,
  'SUPER_ADMIN': 80,
  'ADMIN': 60,
  'MANAGER': 40,
  'USER': 10
};

const AdminRoles = () => {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  const token = sessionStorage.getItem('adminToken');
  const loggedInUser = useMemo(() => {
    if (!token) return null;
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(atob(base64).split('').map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)).join(''));
      return JSON.parse(jsonPayload);
    } catch {
      return null;
    }
  }, [token]);

  const callerRole = loggedInUser?.user_type || 'SUPER_ADMIN';
  const callerRank = ROLE_RANKS[callerRole] || 0;

  const [toast, setToast] = useState({ message: '', type: '', visible: false });
  const showToast = (message, type = 'success') => {
    setToast({ message, type, visible: true });
    setTimeout(() => setToast({ message: '', type: '', visible: false }), 3000);
  };

  useEffect(() => {
    fetchAdmins();
  }, []);

  const fetchAdmins = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${constant.API_BASE_URL}/auth/users`, {
        headers: { Authorization: sessionStorage.getItem('adminToken') }
      });
      if (response.data) {
        // Filter roles like OWNER, SUPER_ADMIN, ADMIN, MANAGER
        const adminUsers = response.data.filter(u => 
          u.user_type === 'OWNER' || u.user_type === 'SUPER_ADMIN' || u.user_type === 'ADMIN' || u.user_type === 'MANAGER'
        );
        setAdmins(adminUsers);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleRoleChange = async (id, newRole) => {
    if (newRole === 'USER') {
      if (!window.confirm("Are you sure you want to revoke Admin/Super Admin access for this user and demote them to a regular USER? This will revoke all their administrative privileges.")) {
        return;
      }
    } else {
      if (!window.confirm(`Are you sure you want to change this user's role to ${newRole}?`)) {
        return;
      }
    }
    try {
      const res = await axios.put(`${constant.API_BASE_URL}/auth/${id}`, {
        user_type: newRole
      }, {
        headers: { Authorization: sessionStorage.getItem('adminToken') }
      });
      if (res.data.success) {
        showToast(newRole === 'USER' ? 'Admin role successfully removed! User is now regular USER.' : `Role updated successfully to ${newRole}`);
        fetchAdmins();
      } else {
        showToast(res.data.message || res.data.errors || "Failed to update role", 'error');
        fetchAdmins();
      }
    } catch (err) {
      showToast(err.response?.data?.errors || err.response?.data?.message || "Failed to update role", 'error');
      fetchAdmins();
    }
  };

  const filteredAdmins = admins.filter(admin => {
    const term = searchTerm.toLowerCase();
    const fullName = `${admin.first_name || ''} ${admin.last_name || ''}`.toLowerCase();
    return fullName.includes(term) || (admin.email && admin.email.toLowerCase().includes(term));
  });

  return (
    <div className="space-y-6 animate-in fade-in duration-300">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-3xl font-extrabold text-slate-800">Admin Role System</h2>
          <p className="text-slate-500 mt-1">Assign roles (Super Admin, Admin, Manager) to staff members and control access permissions.</p>
        </div>
        <button 
          onClick={fetchAdmins}
          className="px-5 py-2.5 bg-white border border-slate-200 text-slate-700 font-semibold rounded-xl hover:bg-slate-50 transition active:scale-95 shadow-sm"
        >
          Refresh Admins
        </button>
      </div>

      <div className="bg-white rounded-[24px] shadow-xl shadow-slate-200/50 border border-slate-100 overflow-hidden">
        {/* Search */}
        <div className="p-6 border-b border-slate-100 flex items-center justify-between gap-4">
          <div className="relative w-full sm:w-72">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
            <input
              type="text"
              placeholder="Search admin users..."
              className="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-pink-500/20 transition"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        {/* Content */}
        {loading ? (
          <div className="p-20 text-center">
            <div className="animate-spin w-10 h-10 border-4 border-pink-500 border-t-transparent rounded-full mx-auto mb-4"></div>
            <p className="text-slate-500">Loading administrators registry...</p>
          </div>
        ) : filteredAdmins.length === 0 ? (
          <div className="p-20 text-center text-slate-400">
            <ShieldAlert size={48} className="mx-auto mb-4 opacity-30" />
            <p>No admin users found.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-50/50 text-slate-400 text-xs font-bold uppercase tracking-wider">
                  <th className="p-6">Admin Profile</th>
                  <th className="p-6">Email Address</th>
                  <th className="p-6">Location</th>
                  <th className="p-6">Role / Permission Level</th>
                  <th className="p-6">Status</th>
                  <th className="p-6 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-sm">
                {filteredAdmins.map((admin) => (
                  <tr key={admin.id} className="hover:bg-slate-50/50 transition">
                    <td className="p-6">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center font-bold">
                          {admin.first_name?.[0]}{admin.last_name?.[0]}
                        </div>
                        <div>
                          <p className="font-bold text-slate-800">{admin.first_name} {admin.last_name}</p>
                          <p className="text-xs font-extrabold text-pink-600">XOXO ID: {admin.xoxo_id || admin.user_id || 'N/A'}</p>
                        </div>
                      </div>
                    </td>
                    <td className="p-6 text-slate-600 font-medium">{admin.email}</td>
                    <td className="p-6">
                      <CountryBadge country={admin.country} />
                    </td>
                    <td className="p-6">
                      <span className={`px-2.5 py-1 rounded-full text-[10px] font-black uppercase inline-flex items-center gap-1 border ${
                        admin.user_type === 'OWNER' ? 'bg-amber-100 text-amber-900 border-amber-300' :
                        admin.user_type === 'SUPER_ADMIN' ? 'bg-red-100 text-red-700 border-red-200' :
                        admin.user_type === 'ADMIN' ? 'bg-green-100 text-green-700 border-green-200' : 
                        'bg-blue-100 text-blue-700 border-blue-200'
                      }`}>
                        {admin.user_type === 'OWNER' && <Crown size={12} className="text-amber-600" />}
                        {admin.user_type}
                      </span>
                    </td>
                    <td className="p-6">
                      <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide ${
                        admin.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                      }`}>
                        {admin.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="p-6 text-right">
                      {(() => {
                        const targetRank = ROLE_RANKS[admin.user_type] || 0;
                        const canModify = callerRank > targetRank;

                        if (!canModify) {
                          return (
                            <span className="inline-flex items-center gap-1 text-[11px] font-bold text-slate-400 bg-slate-50 px-2.5 py-1 rounded-lg border border-slate-200" title="Cannot modify equal or higher ranking accounts">
                              <Lock size={12} /> Protected ({admin.user_type === callerRole ? 'Same Rank' : 'Higher Rank'})
                            </span>
                          );
                        }

                        // Roles strictly below caller rank that caller is permitted to assign
                        const assignableRoles = [
                          callerRank > 80 && { value: 'SUPER_ADMIN', label: 'SUPER_ADMIN' },
                          callerRank > 60 && { value: 'ADMIN', label: 'ADMIN' },
                          callerRank > 40 && { value: 'MANAGER', label: 'MANAGER' },
                          { value: 'USER', label: 'USER (Remove Admin Access)' }
                        ].filter(Boolean);

                        return (
                          <div className="flex justify-end gap-2 items-center">
                            <select
                              className="bg-slate-50 border border-slate-200 rounded-lg px-2 py-1 text-xs font-bold outline-none focus:ring-2 focus:ring-pink-500/20"
                              value={admin.user_type}
                              onChange={(e) => handleRoleChange(admin.id, e.target.value)}
                            >
                              <option value={admin.user_type} disabled>{admin.user_type} (Current)</option>
                              {assignableRoles.map(opt => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                              ))}
                            </select>
                            <button
                              onClick={() => handleRoleChange(admin.id, 'USER')}
                              className="px-2.5 py-1 text-xs font-bold text-rose-600 bg-rose-50 hover:bg-rose-100 border border-rose-200 rounded-lg transition"
                              title="Demote to regular user and revoke admin access"
                            >
                              Revoke Role
                            </button>
                          </div>
                        );
                      })()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Toast Notification */}
      {toast.visible && (
        <div className={`fixed bottom-4 right-4 z-[9999] px-6 py-3 rounded-xl shadow-lg border text-sm font-bold flex items-center gap-2 animate-in slide-in-from-bottom-5 fade-in duration-300 ${toast.type === 'error' ? 'bg-red-50 text-red-600 border-red-100' : 'bg-green-50 text-green-700 border-green-100'}`}>
          {toast.message}
        </div>
      )}
    </div>
  );
};

export default AdminRoles;
